import { Link } from "react-router-dom";
import { Phone, MessageCircle, Calendar, Sparkles, ShieldCheck, Timer, Wrench, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BUSINESS, SERVICES, PRICING_EXAMPLES, FAQS, REVIEWS, AREAS } from "@/lib/business";
import ServiceCard from "@/components/site/ServiceCard";
import ReviewCard from "@/components/site/ReviewCard";
import RatingPill from "@/components/site/RatingPill";
import HeroCarousel from "@/components/site/HeroCarousel";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function Home() {
  const primary = BUSINESS.phones[0];
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden hero-wash noise-overlay" data-testid="home-hero">
        <div className="container-x pt-10 pb-14 sm:pt-16 sm:pb-20 lg:pt-20">
          <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
            <div>
              <Badge className="rounded-full px-3 py-1 border border-pink-200 bg-pink-50 text-[color:var(--icrack-magenta-deep)] hover:bg-pink-50 hover:border-pink-200">
                <Sparkles className="h-3.5 w-3.5 mr-1" /> Fix While You Wait
              </Badge>
              <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900">
                Fast <span style={{ color: "var(--icrack-magenta)" }}>Phone, iPad, Mac &amp; Laptop</span> Repairs in Eastleigh.
              </h1>
              <p className="mt-4 text-lg text-slate-600 leading-relaxed max-w-xl">
                iCrack Solutions is Eastleigh's trusted repair specialist. Walk in for fixed-price
                same-day repairs, or book our <strong>mobile call-out service</strong> — covering
                <strong> all repairs</strong> (phones, tablets, Macs, laptops, PCs) across Eastleigh,
                Southampton, Hampshire &amp; Surrey. <strong>No call-out charge.</strong>
              </p>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-xl">
                <Link to="/book" data-testid="hero-book-repair-button">
                  <Button className="h-12 w-full rounded-xl btn-primary-magenta text-base">
                    <Calendar className="h-5 w-5 mr-2" /> Book Repair
                  </Button>
                </Link>
                <a href={`tel:${primary.tel}`} data-testid="hero-call-now-button">
                  <Button variant="outline" className="h-12 w-full rounded-xl border-slate-200">
                    <Phone className="h-5 w-5 mr-2" style={{ color: "var(--icrack-magenta)" }} /> Call Now
                  </Button>
                </a>
                <a href={BUSINESS.whatsapp.href} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-button">
                  <Button variant="outline" className="h-12 w-full rounded-xl border-slate-200">
                    <MessageCircle className="h-5 w-5 mr-2 text-green-600" /> WhatsApp
                  </Button>
                </a>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <RatingPill score={BUSINESS.rating.score} count={BUSINESS.rating.count} testId="hero-rating-badge" />
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <MapPin className="h-4 w-4" style={{ color: "var(--icrack-magenta)" }} /> 94 Market Street, Eastleigh SO50 5RE
                </div>
              </div>
            </div>

            <HeroCarousel />
          </div>

          {/* Trust chips */}
          <div className="mt-10 chip-scroll md:grid md:grid-cols-5 md:gap-3" data-testid="trust-bar">
            {[
              { icon: Timer, label: "Fix While You Wait" },
              { icon: Wrench, label: "Instant In-Store Repairs" },
              { icon: ShieldCheck, label: "No Fix No Fee" },
              { icon: MapPin, label: "Mobile Call-Out" },
              { icon: Sparkles, label: "No Call-Out Charge" },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2 whitespace-nowrap rounded-full border border-slate-200 bg-white px-3.5 py-2 shadow-sm">
                <Icon className="h-4 w-4" style={{ color: "var(--icrack-magenta)" }} />
                <span className="text-sm font-semibold text-slate-800">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="section-pad" data-testid="home-services-section">
        <div className="container-x">
          <div className="flex items-end justify-between gap-4 mb-8">
            <div>
              <p className="text-sm font-semibold text-[color:var(--icrack-magenta-deep)] uppercase tracking-wider">Repair Services</p>
              <h2 className="mt-1 font-display text-3xl sm:text-4xl font-bold">Fixed fast. Fixed right.</h2>
            </div>
            <Link to="/services" className="link-magenta text-sm font-semibold hidden sm:inline" data-testid="services-view-all">View all services →</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="services-grid">
            {SERVICES.slice(0, 6).map(s => <ServiceCard key={s.slug} service={s} />)}
          </div>
          <div className="mt-6 text-center">
            <Link to="/services" className="link-magenta text-sm font-semibold">See all {SERVICES.length} services →</Link>
          </div>
        </div>
      </section>

      {/* CALL-OUT CTA */}
      <section className="section-pad" data-testid="home-callout-section">
        <div className="container-x">
          <div className="relative overflow-hidden rounded-3xl border border-pink-200 p-6 sm:p-10" style={{ background: "var(--icrack-magenta-soft)" }}>
            <div className="grid gap-8 md:grid-cols-2 md:items-center">
              <div>
                <p className="text-sm font-semibold text-[color:var(--icrack-magenta-deep)] uppercase tracking-wider">Mobile Call-Out Service</p>
                <h2 className="mt-1 font-display text-3xl sm:text-4xl font-bold text-slate-900">Every repair — at your door. No call-out charge.</h2>
                <p className="mt-3 text-slate-700 max-w-xl">Phones, iPads, MacBooks, iMacs, Windows laptops or desktop PCs — our technician can come to your home or office across Eastleigh, Southampton, wider Hampshire and Surrey.</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {BUSINESS.coverage.slice(0, 8).map(area => (
                    <span key={area} className="inline-flex items-center gap-1.5 rounded-full bg-white/80 border border-pink-200 px-3 py-1 text-sm text-slate-800"><MapPin className="h-3.5 w-3.5" style={{ color: "var(--icrack-magenta)" }} />{area}</span>
                  ))}
                </div>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link to="/call-out" data-testid="callout-check-coverage-button">
                    <Button className="h-11 rounded-xl btn-primary-magenta">Check Coverage</Button>
                  </Link>
                  <Link to="/book" data-testid="callout-book-repair-button">
                    <Button variant="outline" className="h-11 rounded-xl border-slate-200 bg-white">Book Call-Out</Button>
                  </Link>
                </div>
              </div>
              <div className="rounded-2xl bg-white p-6 shadow-sm border border-slate-100">
                <h3 className="font-semibold text-slate-900">Simple, transparent pricing</h3>
                <p className="mt-1 text-sm text-slate-600">In-store: fixed-price repairs. Call-out visits: flat labour rate.</p>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-slate-200 p-3">
                    <p className="text-xs text-slate-500 uppercase tracking-wider">In-store</p>
                    <p className="font-display text-2xl font-bold mt-1">Fixed price</p>
                    <p className="text-xs text-slate-500">from £29 + parts</p>
                  </div>
                  <div className="rounded-xl border border-pink-200 bg-pink-50 p-3">
                    <p className="text-xs uppercase tracking-wider" style={{color:"var(--icrack-magenta-deep)"}}>Call-out (home/office)</p>
                    <p className="font-display text-2xl font-bold mt-1" style={{ color: "var(--icrack-magenta)" }}>£{BUSINESS.callOutLabourRate}/hr</p>
                    <p className="text-xs text-slate-500">+ parts • no visit fee</p>
                  </div>
                </div>
                <ul className="mt-4 space-y-2 text-sm text-slate-700">
                  <li className="flex gap-2"><ShieldCheck className="h-4 w-4 text-green-600 mt-0.5" /> Free in-store diagnostic</li>
                  <li className="flex gap-2"><ShieldCheck className="h-4 w-4 text-green-600 mt-0.5" /> No fix, no fee</li>
                  <li className="flex gap-2"><ShieldCheck className="h-4 w-4 text-green-600 mt-0.5" /> No call-out charges</li>
                  <li className="flex gap-2"><ShieldCheck className="h-4 w-4 text-green-600 mt-0.5" /> 3-month warranty on most repairs</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AREAS */}
      <section className="section-pad bg-slate-50" data-testid="home-areas-section">
        <div className="container-x">
          <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
            <div>
              <p className="text-sm font-semibold text-[color:var(--icrack-magenta-deep)] uppercase tracking-wider">Areas we cover</p>
              <h2 className="mt-1 font-display text-3xl sm:text-4xl font-bold">Phone &amp; laptop repair near you</h2>
              <p className="mt-2 text-slate-600 max-w-xl">Local landing pages for every town we serve — tap yours for local pricing, coverage &amp; booking.</p>
            </div>
            <Link to="/areas" className="link-magenta text-sm font-semibold" data-testid="areas-view-all">All areas →</Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {AREAS.slice(0, 10).map(a => (
              <Link key={a.slug} to={`/areas/${a.slug}`} className="group rounded-2xl border border-slate-200 bg-white p-4 hover:border-pink-200 hover:shadow-md transition-shadow" data-testid={`home-area-${a.slug}`}>
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" style={{ color: "var(--icrack-magenta)" }} />
                  <span className="font-semibold text-slate-900">{a.name}</span>
                </div>
                <p className="mt-1 text-xs text-slate-500">{a.region} • {a.postcodePrefix}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING PREVIEW */}
      <section className="section-pad" data-testid="pricing-section">
        <div className="container-x">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold text-[color:var(--icrack-magenta-deep)] uppercase tracking-wider">Transparent Pricing</p>
            <h2 className="mt-1 font-display text-3xl sm:text-4xl font-bold">Common repair prices</h2>
            <p className="mt-3 text-slate-600">In-store repairs are quoted at a fixed price before we begin. Home / office <strong>call-out visits</strong> are charged at a flat <strong>£{BUSINESS.callOutLabourRate}/hour</strong> labour + parts — with no call-out fee.</p>
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pricing-examples">
            {PRICING_EXAMPLES.map(p => (
              <div key={p.title} className="rounded-2xl border border-slate-200 bg-white p-6">
                <h3 className="font-semibold text-slate-900">{p.title}</h3>
                <div className="mt-2 flex items-baseline gap-1">
                  {p.from === 0
                    ? <span className="font-display text-3xl font-bold text-green-600">Free</span>
                    : (<><span className="text-slate-500 text-sm">from</span><span className="font-display text-3xl font-bold" style={{ color: "var(--icrack-magenta)" }}>£{p.from}</span></>)}
                </div>
                <p className="mt-2 text-sm text-slate-600">{p.note}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-slate-500" data-testid="pricing-labour-rate">Prices shown are indicative. Exact quote provided after diagnostic — no obligation.</p>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="section-pad" data-testid="home-reviews-section">
        <div className="container-x">
          <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
            <div>
              <p className="text-sm font-semibold text-[color:var(--icrack-magenta-deep)] uppercase tracking-wider">Google Reviews</p>
              <h2 className="mt-1 font-display text-3xl sm:text-4xl font-bold">Loved by locals in Eastleigh</h2>
              <p className="mt-2 text-slate-600">Real reviews pulled from our Google Business Profile.</p>
            </div>
            <RatingPill score={BUSINESS.rating.score} count={BUSINESS.rating.count} testId="google-rating-summary" />
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {REVIEWS.map((r, i) => <ReviewCard key={i} review={r} index={i} />)}
          </div>
          <div className="mt-6">
            <a className="link-magenta font-semibold text-sm" href={BUSINESS.googleBusinessUrl} target="_blank" rel="noreferrer" data-testid="see-all-reviews-link">Read all {BUSINESS.rating.count} Google reviews →</a>
          </div>
        </div>
      </section>

      {/* FAQ PREVIEW */}
      <section className="section-pad bg-slate-50" data-testid="home-faq-section">
        <div className="container-x max-w-3xl">
          <p className="text-sm font-semibold text-[color:var(--icrack-magenta-deep)] uppercase tracking-wider">FAQ</p>
          <h2 className="mt-1 font-display text-3xl sm:text-4xl font-bold">Answers to common questions</h2>
          <Accordion type="single" collapsible className="mt-6" data-testid="faq-accordion">
            {FAQS.flatMap(g => g.items).slice(0, 6).map((f, i) => (
              <AccordionItem value={`f-${i}`} key={i} data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left text-base font-semibold">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-700 text-sm leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="mt-6">
            <Link to="/faq" className="link-magenta font-semibold text-sm" data-testid="faq-view-all-link">See all FAQs →</Link>
          </div>
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="section-pad" data-testid="home-contact-cta">
        <div className="container-x">
          <div className="rounded-3xl border border-slate-200 p-6 sm:p-10 bg-white grid gap-8 md:grid-cols-2 md:items-center">
            <div>
              <p className="text-sm font-semibold text-[color:var(--icrack-magenta-deep)] uppercase tracking-wider">Get in touch</p>
              <h2 className="mt-1 font-display text-3xl sm:text-4xl font-bold">Ready to fix your device?</h2>
              <p className="mt-3 text-slate-600 max-w-lg">Pop in for a walk-in repair, call us, WhatsApp us, or book online. Whatever works for you.</p>
              <div className="mt-5 flex flex-wrap gap-3">
                <a href={`tel:${primary.tel}`} data-testid="contact-cta-call"><Button className="h-11 rounded-xl btn-primary-magenta"><Phone className="h-4 w-4 mr-2"/>Call {primary.display}</Button></a>
                <a href={BUSINESS.whatsapp.href} target="_blank" rel="noreferrer" data-testid="contact-cta-whatsapp"><Button variant="outline" className="h-11 rounded-xl border-slate-200"><MessageCircle className="h-4 w-4 mr-2 text-green-600"/>WhatsApp</Button></a>
                <Link to="/book" data-testid="contact-cta-book"><Button variant="outline" className="h-11 rounded-xl border-slate-200"><Calendar className="h-4 w-4 mr-2" style={{ color: "var(--icrack-magenta)" }}/>Book Online</Button></Link>
              </div>
            </div>
            <div className="aspect-video overflow-hidden rounded-2xl border border-slate-200">
              <iframe
                title="iCrack Solutions on Google Maps"
                src={BUSINESS.googleMapsEmbed}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-google-map"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
